import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Grid,
  Theme,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { cloneInspection } from 'store/request/inspections/actions';
import { InspectionStatuses } from 'types';
import { parseInspectionInput, ParsedInspectionInput } from 'utils/inspectionUrlParser';
import styles from './InspectionCloningModal.module.css';

// Constants
const DELAYED_INFO_TIMEOUT_MS = 10000; // 10 seconds

const useStyles = makeStyles()((theme: Theme) => ({
  dialogContent: {
    padding: '24px',
    background: 'var(--gds-color-cool-gray-700)',
  },
  dialogTitle: {
    background: 'var(--gds-color-cool-gray-700)',
    paddingX: '24px',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '-16px',
  },
  buttonContainer: {
    paddingTop: theme.spacing(2),
  },
}));

interface InspectionCloningModalProps {
  open: boolean;
  onClose: () => void;
}

const InspectionCloningModal: React.FC<InspectionCloningModalProps> = ({ open, onClose }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inspectionInput, setInspectionInput] = useState<string>('');
  const [parsedInput, setParsedInput] = useState<ParsedInspectionInput | null>(null);
  const [newInspectionName, setNewInspectionName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showDelayedInfo, setShowDelayedInfo] = useState<boolean>(false);
  const shouldNavigateRef = useRef<boolean>(true);
  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timer when component unmounts or modal closes
  useEffect(() => {
    if (!open) {
      // Clear timer when modal closes
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setShowDelayedInfo(false);
    }

    return () => {
      // Clear timer on unmount
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, [open]);

  // Start timer when loading begins
  useEffect(() => {
    if (loading) {
      // Reset delayed info
      setShowDelayedInfo(false);
      // Start 10 second timer
      delayTimerRef.current = setTimeout(() => {
        setShowDelayedInfo(true);
      }, DELAYED_INFO_TIMEOUT_MS);
    } else {
      // Clear timer and hide info when loading stops
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setShowDelayedInfo(false);
    }
  }, [loading]);

  // Handle input change and parse it
  const handleInputChange = (value: string) => {
    setInspectionInput(value);
    const parsed = parseInspectionInput(value);
    setParsedInput(parsed);
  };

  const handleCloneInspection = async () => {
    if (!parsedInput?.isValid || !parsedInput.inspectionId) {
      return;
    }

    // Start loading
    setLoading(true);
    // Reset the navigation flag when starting a new request
    shouldNavigateRef.current = true;

    try {
      await dispatch(
        cloneInspection(
          parsedInput.inspectionId,
          {
            newName: newInspectionName.trim() || undefined,
          },
          (responseData: any) => {
            // Only navigate if the modal is still open and user hasn't canceled
            if (shouldNavigateRef.current) {
              const url =
                responseData.status === InspectionStatuses.completed
                  ? `/inspection/${responseData.id}/result`
                  : `/inspection/${responseData.id}`;
              navigate(url);
              handleClose();
            }
          },
        ),
      );

      // Reset form after successful clone (only if we didn't navigate away)
      if (shouldNavigateRef.current) {
        handleInputChange('');
        setNewInspectionName('');
      }
    } catch (error) {
      // Error handling is done in the action's onError callback
      console.error('Error cloning inspection:', error);
    } finally {
      // Always stop loading, regardless of success or error
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Cancel navigation if request is still in progress
    shouldNavigateRef.current = false;
    // Reset loading state
    setLoading(false);
    handleInputChange('');
    setNewInspectionName('');
    onClose(); // Parent will handle Redux state
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Grid sx={{ background: 'var(--gds-color-cool-gray-700)' }}>
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">Clone Inspection</Typography>
          <IconButton aria-label="close" onClick={handleClose} size="small">
            <CloseIcon color="primary" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <GVTypography variant="body2" color="textPrimary" gutterBottom>
            Clone an existing inspection to create a copy with all associated files, preparation steps, and results. The
            cloned inspection will be owned by you.
          </GVTypography>

          <Box className={styles.limitations}>
            <GVTypography variant="body2" color="textSecondary" sx={{ fontWeight: 600, marginBottom: 1 }}>
              Limitations:
            </GVTypography>
            <GVTypography variant="body2" color="textSecondary">
              • Inspections can only be cloned within the same environment (Production, Beta, etc.)
              <br />
              • Cross-tenant inspections retain their custom dictionaries and profiles, but you cannot suggest words to
              dictionaries from other organizations
              <br />• Custom statuses will not be applied to the cloned inspection results
            </GVTypography>
          </Box>

          <div className={styles.form}>
            <GVTextField
              label="Inspection ID or URL"
              value={inspectionInput}
              onValueChange={(value) => handleInputChange(value ?? '')}
              placeholder="Enter inspection ID, full URL, or shortened URL"
              isDisabled={loading}
              id="inspection-id-input"
              hasError={Boolean(inspectionInput.trim() && !parsedInput?.isValid)}
              message={
                inspectionInput.trim() && !parsedInput?.isValid
                  ? parsedInput?.error
                  : 'Supports inspection IDs, full URLs, and shortened URLs'
              }
            />

            <GVTextField
              className={styles.field}
              label="New Inspection Name (Optional)"
              value={newInspectionName}
              onValueChange={(value) => setNewInspectionName(value ?? '')}
              placeholder="Enter a new name for the cloned inspection"
              message="If left empty, the original name will be used"
              isDisabled={loading}
              id="new-inspection-name-input"
            />

            {showDelayedInfo && (
              <Box
                data-testid="slow_running_inspection"
                sx={{
                  marginTop: 2,
                  padding: 2,
                  backgroundColor: '#2C5AA0',
                  borderRadius: '4px',
                  border: '1px solid #4A90E2',
                }}
              >
                <GVTypography variant="body2" color="white" sx={{ fontWeight: 600, marginBottom: 1 }}>
                  Taking longer than expected?
                </GVTypography>
                <GVTypography variant="body2" color="white">
                  Cloning can take several minutes as we copy all files, differences, and job data. You can safely close
                  this modal - the cloning process will continue in the background and your new inspection will appear
                  in your sessions list when complete.
                </GVTypography>
              </Box>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
          <Button onClick={handleClose} color="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloneInspection}
            disabled={!parsedInput?.isValid || loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {loading ? 'Cloning...' : 'Clone Inspection'}
          </Button>
        </DialogActions>
      </Grid>
    </Dialog>
  );
};

export default InspectionCloningModal;
